import type { ChatMessage, WidgetConfig } from './types'

/**
 * Cliente HTTP pro pilar-agent. Sem SDK, sem dependência — fetch() simples
 * pros endpoints do canal web (ver pilar-agent/src/channels/web/README.md):
 * GET .../config pra se renderizar, POST .../chat pra conversar, GET
 * .../history pra restaurar a conversa ao montar, e um sendBeacon (não
 * fetch — precisa sobreviver ao fechamento da aba) pra .../clear-history.
 */
export class WidgetApiError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
  }
}

export async function fetchWidgetConfig(apiUrl: string, tenantId: string): Promise<WidgetConfig> {
  const res = await fetch(`${apiUrl}/widget/${encodeURIComponent(tenantId)}/config`)
  if (!res.ok) {
    throw new WidgetApiError(`config respondeu ${res.status}`, res.status)
  }
  return res.json()
}

export async function sendChatMessage(
  apiUrl: string,
  tenantId: string,
  sessionId: string,
  text: string
): Promise<string> {
  const res = await fetch(`${apiUrl}/widget/${encodeURIComponent(tenantId)}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, text }),
  })
  if (!res.ok) {
    throw new WidgetApiError(`chat respondeu ${res.status}`, res.status)
  }
  const data = (await res.json()) as { reply: string }
  return data.reply
}

export async function fetchHistory(
  apiUrl: string,
  tenantId: string,
  sessionId: string
): Promise<ChatMessage[]> {
  const res = await fetch(
    `${apiUrl}/widget/${encodeURIComponent(tenantId)}/history?sessionId=${encodeURIComponent(sessionId)}`
  )
  if (!res.ok) {
    throw new WidgetApiError(`history respondeu ${res.status}`, res.status)
  }
  const data = (await res.json()) as { messages: Array<{ role: 'user' | 'assistant'; content: string }> }
  return data.messages.map((m) => ({ role: m.role, text: m.content }))
}

/**
 * fetch() é cancelado quando a página descarrega — por isso sendBeacon, feito
 * pra exatamente esse caso (best-effort, sem esperar resposta, sobrevive ao
 * unload). Falha silenciosa se o navegador não suportar: pior caso, o
 * histórico só expira pelo TTL normal (7 dias) em vez de encurtar na hora.
 */
export function sendClearHistoryBeacon(apiUrl: string, tenantId: string, sessionId: string): void {
  try {
    navigator.sendBeacon(
      `${apiUrl}/widget/${encodeURIComponent(tenantId)}/clear-history`,
      JSON.stringify({ sessionId })
    )
  } catch {
    // sendBeacon ausente/bloqueado — sem tratamento, é best-effort.
  }
}
